import React from 'react';
import { useTheme } from '../../theme/ThemeContext';
import { CloseIcon, EditIcon } from '../../helpers/Icons';
import {
  calcSingleProfit,
  getOrderOpenPrice,
  getOrderTypeLabel,
  getOrderVolumeLots,
  isBuyOrder,
  isPendingOrder,
} from '../../services/platform/TradingStore';
import {
  formatDateTime,
  formatPrice,
  getOrderSL,
  getOrderTP,
  getOrderTimestamp,
  getTicketValue,
} from './portfolioUtils';
import { usePriceSubscription } from '../../hooks/usePriceSubscription';

const TradeRow = ({ order, onEdit, onCloseRequested, onDetail }) => {
  const theme = useTheme();
  const price = usePriceSubscription(order.symbol);
  const typeLabel = getOrderTypeLabel(order);
  const buyOrder = isBuyOrder(order);
  const pendingOrder = isPendingOrder(order);
  const typeColor = buyOrder ? theme.pos : theme.neg;
  const vol = getOrderVolumeLots(order).toFixed(2);
  const ticket = getTicketValue(order);
  const openPrice = getOrderOpenPrice(order);
  const currentPrice = price ? (buyOrder ? price.bid : price.ask) : null;
  const profit = pendingOrder ? null : (price ? calcSingleProfit(order, currentPrice) : Number(order?.profit ?? 0));
  const profitColor = profit >= 0 ? theme.pos : theme.neg;
  const sl = getOrderSL(order);
  const tp = getOrderTP(order);
  const openedAt = getOrderTimestamp(order);

  return (
    <div
      onDoubleClick={() => onDetail(order, 'open')}
      style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '11px 12px', borderBottom: `1px solid ${theme.border}22`, transition: 'background 0.2s', cursor: 'pointer' }}
    >
      <div style={{ width: 4, height: 32, borderRadius: 2, background: typeColor, flexShrink: 0 }} />
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 2 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: theme.text }}>{order.symbol}</span>
          <span style={{ fontSize: 10, fontWeight: 700, color: typeColor, background: `${typeColor}15`, padding: '1px 5px', borderRadius: 4, textTransform: 'uppercase' }}>{typeLabel}</span>
          <span style={{ fontSize: 10, color: theme.textM, fontWeight: 500 }}>#{ticket}</span>
        </div>
        <div style={{ fontSize: 11, color: theme.textM, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '4px 8px' }}>
          <span style={{ fontWeight: 600, color: theme.textS }}>{vol} lots</span>
          <span>@ <span style={{ fontFamily: theme.mono, color: theme.textS }}>{formatPrice(openPrice, order?.digits ?? 5)}</span></span>
          <span>Now 
            <span style={{ marginLeft: 2, fontSize: 11, fontWeight: 600, fontFamily: theme.mono, color: theme.textS }}>
              {price ? formatPrice(currentPrice, order?.digits ?? 5) : '--'}
            </span>
            </span>
          {(sl > 0 || tp > 0) && (
            <div style={{ display: 'flex', gap: 6 }}>
              {sl > 0 && <span style={{ color: theme.neg, fontSize: 10 }}>SL: <span style={{ fontFamily: theme.mono }}>{sl.toFixed(order?.digits ?? 5)}</span></span>}
              {tp > 0 && <span style={{ color: theme.pos, fontSize: 10 }}>TP: <span style={{ fontFamily: theme.mono }}>{tp.toFixed(order?.digits ?? 5)}</span></span>}
            </div>
          )}
          <span>{formatDateTime(openedAt)}</span>
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        {profit !== null && (
          <span style={{ fontFamily: theme.mono, fontVariantNumeric: 'tabular-nums', fontWeight: 700, color: profitColor, fontSize: 12, textAlign: 'right', minWidth: 56 }}>
            {profit >= 0 ? '+' : ''}{profit.toFixed(2)}
          </span>
        )}
        {/* <span style={{ fontFamily: theme.mono, fontVariantNumeric: 'tabular-nums', fontWeight: 700, color: bookedProfit >= 0 ? theme.pos : theme.neg, fontSize: 11, minWidth: 52, textAlign: 'right' }}>
          {bookedProfit >= 0 ? '+' : ''}{bookedProfit.toFixed(2)}
        </span> */}
        <div style={{ display: 'flex', gap: 4 }}>
          <button onClick={(e) => { e.stopPropagation(); onEdit(order); }} style={{ width: 26, height: 26, borderRadius: 6, background: theme.card2, border: `1px solid ${theme.border}`, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <EditIcon s={12} c={theme.textS} />
          </button>
          <button onClick={(e) => { e.stopPropagation(); onCloseRequested(order); }} style={{ width: 26, height: 26, borderRadius: 6, background: theme.card2, border: `1px solid ${theme.border}`, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <CloseIcon s={12} c={theme.textS} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default React.memo(TradeRow);
